<template>
  <div class="opportunity-source-tender__base-card">
    <div ref="topAnchor" v-if="!hideViewTender">
      <div class="opportunity-source-tender__header">
        <span class="opportunity-source-tender__title">{{ $t('page.opportunities.data-table-titles.source') }}</span>
        <div v-if="tender" class="opportunity-source-tender__header-actions">
          <BaseButton isGrey isSquare has-icon-right isSmall @click.native="openTenderDetails"
            ><span>{{ $t('page.opportunity-details.view-tender') }}</span
            ><BaseIcon name="arrow-right"
          /></BaseButton>
        </div>
      </div>
    </div>
    <div class="opportunity-source-tender__card">
      <BaseCircularLoadingBar v-if="tenderPending" :size="14" :width="2" color="accent" />
      <div v-else-if="tender" class="opportunity-source-tender__info-section">
        <div v-if="canDisplayScore" class="tender-title__score-status">
          <span class="opportunity-source-tender__score-section">
            <DynamicThermometer :value="tender.score" isLarge />
          </span>
        </div>

        <TenderStatus :status="tender.status" />
        <TenderTimeLeft :status="tender.status" :daysBeforeClosing="tender.daysBeforeClosing" useShortDate />

        <div class="opportunity-source-tender__reference-number">
          <span class="label">{{ $t('tender-details.reference-num-label') }}</span>
          <BaseTag tone="neutral" preset="soft">{{ formattedReferenceNumber }}</BaseTag>
        </div>

        <div class="opportunity-source-tender__closing-date">
          <span class="label">{{ $t('tender-details.informations.closing-date') }}</span>
          <BaseTag
            v-if="tender && tender.completeClosingDate"
            tone="neutral"
            preset="soft"
            v-tooltip="{
              content: tender.closingDateDetails,
              trigger: isMobile ? 'click' : 'focus hover',
            }"
          >
            {{ tender.completeClosingDate }}
          </BaseTag>
          <span v-else class="opportunity-detail-page__card-empty-text">{{ $t('basic-words.unavailable') }}</span>
        </div>

        <div v-if="tender && tender.source && tender.source.id" class="opportunity-source-tender__source">
          <span class="label">{{ $t('page.opportunities.data-table-titles.source') }}</span>
          <BaseLink
            id="tender-source-url"
            target-blank
            :href="tender.source.url"
            v-tooltip="{ content: $t('tender-details.origin-site-access'), placement: 'top', offset: '8px' }"
          >
            <PlatformIcon :platform="tender.source.id" class="tender-title__platform-icon" />
            {{ platformName(tender.source.id) }}
          </BaseLink>
        </div>

        <div
          v-if="relatedSources.length"
          class="opportunity-source-tender__source opportunity-source-tender__source--related"
        >
          <span class="label">{{ $t('tender-details.related-sources') }}</span>
          <TenderRelatedSources :related-sources="relatedSources" />
        </div>
      </div>
      <div v-else class="opportunity-detail-page__card-empty-text">{{ $t('basic-words.unavailable') }}</div>
    </div>
    <div v-if="tenderPending || tender" class="opportunity-source-tender__card">
      <BaseCircularLoadingBar v-if="tenderPending" :size="14" :width="2" color="accent" />
      <TenderEmitterInformation v-else :emitter="tender.procuringEntity" />
    </div>

    <div v-if="tenderPending || tender" class="opportunity-source-tender__card">
      <BaseCircularLoadingBar v-if="tenderPending" :size="14" :width="2" color="accent" />
      <TenderBidders v-else compact @action-succeeded="onActionSucceeded" />
    </div>
    <div v-if="tenderPending || tender" class="opportunity-source-tender__card">
      <BaseCircularLoadingBar v-if="tenderPending" :size="14" :width="2" color="accent" />
      <TenderGeneralInformation v-else :tender="tender" />
    </div>
    <div v-if="tenderPending || tender" class="opportunity-source-tender__card">
      <BaseCircularLoadingBar v-if="tenderPending" :size="14" :width="2" color="accent" />
      <TenderDocuments v-if="tender && !tenderPending" :tender="tender" />
    </div>
  </div>
</template>

<script>
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseLink from '@/components/base/BaseLink';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import PlatformIcon from '@/components/PlatformIcon';
import TenderBidders from '@/components/page-tender-details/TenderBidders';
import TenderDocuments from '@/components/page-tender-details/TenderDocuments';
import TenderEmitterInformation from '@/components/page-tender-details/TenderEmitterInformation.vue';
import TenderGeneralInformation from '@/components/page-tender-details/TenderGeneralInformation.vue';
import TenderRelatedSources from '@/components/page-tender-details/TenderRelatedSources.vue';
import TenderStatus from '@/components/page-tender-details/TenderStatus.vue';
import TenderTimeLeft from '@/components/page-tender-details/TenderTimeLeft.vue';
import DynamicThermometer from '@/components/util/DynamicThermometer';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import PlatformName from '@/mixins/platform-name-mixin';

import { tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { mapActions, mapGetters } from 'vuex';
import { removeMarks } from '@/util/mark-util';
import { sameTenderSources } from '@/util/related-sources-util';

export default {
  name: 'SourceTender',
  mixins: [IsMobileMixin, PlatformName],
  components: {
    BaseCircularLoadingBar,
    BaseLink,
    BaseTag,
    BaseIcon,
    BaseButton,
    PlatformIcon,
    TenderBidders,
    TenderDocuments,
    TenderEmitterInformation,
    TenderGeneralInformation,
    TenderRelatedSources,
    TenderStatus,
    TenderTimeLeft,
    DynamicThermometer,
  },
  props: {
    tenderId: String,
    showTitle: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideViewTender: {
      type: Boolean,
      required: false,
      default: false,
    },
    competitorId: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    ...mapGetters('tender', ['tender']),
    ...mapGetters('tender', { tenderPending: 'pending' }),
    canDisplayScore() {
      return this.tenantHasTenderScoringAccess() && this.tender && this.tender.score !== null;
    },
    formattedReferenceNumber() {
      return this.referenceNumber.replace('#', '');
    },
    referenceNumber() {
      return this.tender && this.tender.source && this.tender.source.referenceNumber
        ? this.$t('tender-details.reference-num', { number: this.tender.source.referenceNumber })
        : this.$t('basic-words.none');
    },
    relatedSources() {
      return sameTenderSources(this.tender);
    },
  },
  methods: {
    ...mapActions('biDashboard', { getBiTender: 'getTender', getAllCompetitors: 'getAllCompetitors' }),
    ...mapActions('tender', ['getTender', 'resetTender']),
    removeMarks,
    tenantHasTenderScoringAccess,
    platformName(tenderSourceId) {
      return this.getPlatformName(tenderSourceId);
    },
    fetchTender() {
      if (!this.tender || this.tender.id !== this.tenderId) {
        this.getTender({ id: this.tenderId, keywords: null });
      }
      if (this.tenderId) {
        this.getBiTender({ id: this.tenderId, competitorId: this.competitorId });
      }
    },
    openTenderDetails() {
      this.$router.push({ name: 'tender-details', params: { id: this.tenderId } });
    },
    onActionSucceeded() {
      this.fetchTender();
      this.getAllCompetitors();
      this.$emit('tender-updated');
    },
  },
  created() {
    this.fetchTender();
  },
  destroyed() {
    this.resetTender();
  },
  watch: {
    tenderId() {
      this.fetchTender();
    },
  },
};
</script>

<style lang="scss">
.opportunity-source-tender {
  &__base-card {
    padding: 32px;
    border-radius: 8px;
    background: $color-white;
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: fit-content;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap-reverse;
    gap: 8px;
  }

  &__title {
    display: flex;
    justify-content: space-between;
    color: $color-normal;
    font-size: 20px;
    font-weight: 800;
    gap: 8px;
  }

  &__score-section {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
  }

  &__info-section {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    min-width: 0;
  }

  &__card {
    border-radius: 5px;
    border: 1px solid $color-slate-200;
    padding: 16px;
    display: flex;
    flex-wrap: wrap;
    min-width: 0;

    .title,
    .tender-status__subtitle,
    .tender-title__subtitle {
      color: $color-dark-blue;
      font-size: 12px;
      font-weight: 400;
    }

    .subtitle {
      color: $color-normal;
      font-size: $font-size-small;
      font-weight: bold;
      letter-spacing: normal;
      line-height: 1.43;
      margin-bottom: 0.3rem;
      padding-top: 1rem;
    }

    .tender-status__status,
    .tender-status__time-left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 4px;
      height: 48px;
    }

    .tender-potential-bidders,
    .tender-emitter-information,
    .tender-bidders,
    .tender-documents {
      padding: 0;
      margin-bottom: 0;
      width: 100%;
    }

    .tender-title__score-status {
      display: flex;
      align-items: center;
      position: sticky;
    }
  }

  &__closing-date {
    display: flex;
    flex-direction: column;
    height: 48px;
    justify-content: flex-start;
    gap: 4px;

    .label {
      color: $color-dark-blue;
      font-size: 12px;
    }

    &--dates {
      display: inline-flex;
      flex-direction: row;
      color: $color-normal-pale;
      font-size: $font-size-small;
      line-height: 1.43;
      gap: 4px;
      cursor: help;
    }
  }

  &__reference-number {
    display: flex;
    height: 100%;
    flex-direction: column;
    height: 48px;
    justify-content: flex-start;
    gap: 4px;

    .label {
      color: $color-dark-blue;
      font-size: 12px;
    }
  }

  &__source {
    display: flex;
    flex-direction: column;
    height: 48px;
    justify-content: flex-start;
    gap: 4px;

    .label {
      color: $color-dark-blue;
      font-size: 12px;
    }

    .base-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: $font-size-small;
      line-height: 1.2;
    }

    .base-link__element {
      padding: 0;
      gap: 4px;
    }

    .tender-title__platform-icon,
    .tender-related-sources__icon {
      width: 12px;
      height: 12px;
    }

    .tender-related-sources__icon {
      padding-right: 0;
    }

    // As large as a platform's square, so a platform without one keeps the same gap to its name.
    .platform-icon__fallback {
      height: 14px;
      width: 14px;

      &--icon {
        font-size: 10px;
      }
    }

    .tender-related-sources__text {
      gap: 4px;
      font-size: $font-size-small;
      line-height: 1.2;
    }

    // Grows a line per source that wraps, instead of running into the card below.
    &--related {
      height: auto;
      min-height: 48px;
    }
  }
}
</style>
